/*
 * El test en una ventana, desde cualquier pagina (guia de Tamara, 24/09/2026,
 * pag. 2). Ver `sections/natnatura-test-ventana.liquid`.
 *
 * Al primer clic en un enlace al test se pide `/pages/cuestionario` (con el
 * prefijo del idioma), se saca `[data-nn-test]` y se monta aqui con
 * `NNTest.montar(nodo, { modo: 'ventana' })`. La respuesta queda en memoria
 * mientras dure la pagina; cada apertura monta un test nuevo desde la
 * portada, porque al cerrar no se guarda nada.
 *
 * Siempre `showModal()`: foco atrapado, Esc, y la pagina inerte debajo. El
 * scroll se bloquea con el atributo de Horizon (`scroll-lock` en `<html>`,
 * que tambien para `.page-wrapper`), como el aviso de la newsletter; y ese
 * aviso ya no se abre mientras haya otro `dialog[open]`.
 *
 * Por delegacion en `document`: `theme dev` sustituye las secciones al
 * recargar y los oyentes del nodo moririan con el.
 */
(() => {
  if (window.NNTestVentana) return;
  window.NNTestVentana = true;

  const RUTAS = ['/pages/test', '/pages/cuestionario'];
  const dialogo = () => document.querySelector('dialog[data-nn-test-ventana]');
  let html = null; // la pagina del cuestionario, pedida una vez
  let pendiente = null; // la peticion en curso (una sola aunque se abra dos veces)
  let apertura = 0; // cada apertura tiene su numero: solo monta la ultima
  let disparador = null;

  /** La ruta sin el prefijo de idioma (`/pt/pages/test` -> `/pages/test`). */
  const sinIdioma = (ruta) => {
    const raiz = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const r = ruta.replace(/\/+$/, '');
    if (raiz !== '/' && r.startsWith(raiz.replace(/\/$/, ''))) return r.slice(raiz.replace(/\/$/, '').length) || '/';
    return r.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/pages\/)/i, '');
  };

  const enElCuestionario = () => sinIdioma(window.location.pathname) === '/pages/cuestionario';

  const pedir = (url) => {
    if (html) return Promise.resolve(html);
    if (!pendiente) {
      pendiente = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then((r) => {
          if (!r.ok) throw new Error(`cuestionario: ${r.status}`);
          return r.text();
        })
        .then((texto) => {
          html = texto;
          return texto;
        })
        .finally(() => {
          pendiente = null;
        });
    }
    return pendiente;
  };

  const esEscritorio = () => window.matchMedia('(min-width: 750px)').matches;

  // `natnatura-test.js` (56 KB) se pide al abrir la ventana, a la vez que la
  // pagina del cuestionario, y no al cargar cada pagina de la tienda, que es
  // como venia hasta el 22/10/2026 (PageSpeed). En la pagina del cuestionario
  // ya llega con su bloque y aqui no se vuelve a pedir.
  let cargaTest = null;
  const traerTest = (src) => {
    if (window.NNTest && window.NNTest.montar) return Promise.resolve();
    if (!src) return Promise.reject(new Error('sin natnatura-test.js'));
    if (!cargaTest) {
      cargaTest = new Promise((ok, mal) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => ok();
        s.onerror = () => {
          cargaTest = null;
          s.remove();
          mal(new Error('natnatura-test.js no cargo'));
        };
        document.head.append(s);
      });
    }
    return cargaTest;
  };

  // En movil la ventana es una pantalla: el «Atras» del navegador la cierra.
  // Al abrir se apila una entrada con la misma URL (`nnTest`); `popstate` la
  // cierra, y si se cierra con la X, Esc o al comprar se quita esa entrada con
  // `history.back()`, solo si sigue arriba (nunca se navega fuera).
  let conHistoria = false;
  let limpia = true; // la ultima apertura ya se recogio
  // Cada apertura marca su entrada con un valor propio: una recarga con la
  // ventana abierta conserva `history.state`, y la entrada de debajo llevaria
  // una marca vieja que haria creer que la nuestra sigue arriba.
  let marca = null;
  const nuestra = () => !!(marca && history.state && history.state.nnTest === marca);
  if (history.state && history.state.nnTest) {
    const { nnTest, ...resto } = history.state;
    history.replaceState(resto, '');
  }

  /**
   * Lo que se deshace SIEMPRE que la ventana se cierra, venga de donde venga
   * el cierre (X, Esc, compra, «Atras» del navegador o un `close()` ajeno):
   * se atiende tambien desde el evento `close` del dialogo.
   */
  const recoger = (d) => {
    if (limpia) return;
    limpia = true;
    apertura += 1; // una carga en curso ya no es de esta ventana
    d.classList.remove('nn-test-ventana--cerrando');
    d.querySelectorAll('[data-nn-test]').forEach((n) => n.remove());
    if (!document.querySelector('dialog[open][scroll-lock], dialog.nn-captacion--modal[open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
    if (disparador && disparador.isConnected && typeof disparador.focus === 'function') {
      disparador.focus({ preventScroll: true });
    }
    if (conHistoria) {
      conHistoria = false;
      if (nuestra()) history.back();
      marca = null;
    }
  };

  const abrir = async (origen) => {
    const d = dialogo();
    if (!d || d.open) return;
    const esta = ++apertura;
    // Sigue viva si la ventana esta abierta y nadie la ha vuelto a abrir
    // despues (cerrar y abrir mientras carga no monta dos tests).
    const viva = () => d.open && esta === apertura;
    disparador = origen || document.activeElement;
    const cuerpo = d.querySelector('[data-nn-test-cuerpo]');
    const cargando = d.querySelector('[data-nn-test-cargando]');
    cuerpo.querySelectorAll('[data-nn-test]').forEach((n) => n.remove());
    if (cargando) cargando.hidden = false;
    // El aviso de la newsletter no se queda debajo (en movil es una tarjeta
    // no modal y asomaba por encima de la pantalla del test).
    document.querySelectorAll('dialog.nn-captacion[open]').forEach((c) => {
      c.classList.remove('nn-captacion--cerrando');
      c.close();
    });
    d.classList.remove('nn-test-ventana--cerrando');
    d.classList.toggle('nn-test-ventana--movil', !esEscritorio());
    d.showModal();
    limpia = false;
    document.documentElement.setAttribute('scroll-lock', '');
    if (!esEscritorio() && !conHistoria) {
      marca = `${Date.now()}-${apertura}`;
      history.pushState({ ...(history.state || {}), nnTest: marca }, '');
      conHistoria = true;
    }
    // El script del test se pide ya, en paralelo con la pagina.
    const guion = traerTest(d.dataset.testJs);
    guion.catch(() => {});
    try {
      const texto = await pedir(d.dataset.url);
      const doc = new DOMParser().parseFromString(texto, 'text/html');
      const test = doc.querySelector('[data-nn-test]');
      if (!test) throw new Error('cuestionario sin test');
      // Si se cerro (o se reabrio) mientras cargaba, esta apertura no monta.
      if (!viva()) return;
      await Promise.all([traerEstilos(doc), guion]);
      if (!viva()) return;
      const nodo = document.importNode(test, true);
      if (cargando) cargando.hidden = true;
      cuerpo.append(nodo);
      if (window.NNTest && window.NNTest.montar) window.NNTest.montar(nodo, { modo: 'ventana' });
      const primero = nodo.querySelector('[data-empezar]');
      if (primero) primero.focus({ preventScroll: true });
    } catch (e) {
      // Sin ventana, a la pagina: que el test no se quede sin salida. Pero no
      // si quien la abrio ya la cerro (o la volvio a abrir).
      console.warn('[natnatura-test-ventana]', e);
      if (viva()) window.location.href = d.dataset.url;
    }
  };

  /**
   * Los estilos del test viajan con la pagina del test (09/10/2026). Shopify
   * compila los `{% stylesheet %}` de los bloques en una hoja POR PAGINA
   * (`compiled_assets/styles.css?v=…`): la de la portada no lleva las reglas
   * de `natnatura-test` y la ventana pintaba el test sin estilos (pantallas
   * apiladas, «Siguiente» encima del campo, opciones sin negrita), en movil y
   * en escritorio. Se copian las hojas de la pagina del test que esta pagina
   * no tenga y se espera a que carguen antes de montar, para que no parpadee.
   */
  const traerEstilos = (doc) => {
    const tengo = new Set([...document.querySelectorAll('link[rel="stylesheet"]')].map((l) => l.href));
    const nuevas = [...doc.querySelectorAll('link[rel="stylesheet"]')]
      .map((l) => new URL(l.getAttribute('href'), d0Url()).href)
      .filter((href) => href.includes('/compiled_assets/') && !tengo.has(href));
    return Promise.all(
      nuevas.map(
        (href) =>
          new Promise((listo) => {
            const l = document.createElement('link');
            l.rel = 'stylesheet';
            l.href = href;
            l.dataset.nnTestEstilos = '';
            l.onload = l.onerror = () => listo();
            // En el mismo punto de la cascada que la hoja compilada de esta
            // pagina (antes de natnatura-brand.css), no al final del head.
            const propia = document.querySelector('link[rel="stylesheet"][href*="/compiled_assets/"]');
            if (propia) propia.after(l);
            else document.head.prepend(l);
          })
      )
    );
  };
  const d0Url = () => window.location.href;

  const cerrar = (listo) => {
    const d = dialogo();
    if (!d || !d.open) {
      if (d) recoger(d);
      if (listo) listo();
      return;
    }
    let hecho = false;
    const fin = () => {
      if (hecho) return;
      hecho = true;
      d.close();
      recoger(d);
      if (listo) listo();
    };
    apertura += 1; // una carga en curso ya no es de esta ventana
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fin();
      return;
    }
    d.classList.add('nn-test-ventana--cerrando');
    // Solo la animacion del propio dialogo (las de dentro tambien suben).
    const alAcabar = (e) => {
      if (e.target !== d) return;
      d.removeEventListener('animationend', alAcabar);
      fin();
    };
    d.addEventListener('animationend', alAcabar);
    window.setTimeout(fin, 300); // la salida dura 220 ms
  };

  // `close` no burbujea: en captura, desde `document`, para cualquier cierre.
  document.addEventListener(
    'close',
    (e) => {
      if (e.target === dialogo()) recoger(e.target);
    },
    true,
  );

  // «Atras» del navegador con la ventana abierta en movil: se cierra, y la
  // entrada ya la ha quitado el propio navegador.
  window.addEventListener('popstate', () => {
    const d = dialogo();
    if (!conHistoria || nuestra()) return;
    conHistoria = false;
    marca = null;
    if (d && d.open) cerrar();
  });

  // Los enlaces al test (y cualquier [data-nn-abrir-test]) abren la ventana.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (window.Shopify && window.Shopify.designMode) return;
    const boton = e.target.closest('[data-nn-abrir-test]');
    const enlace = boton ? null : e.target.closest('a[href]');
    if (!boton && !enlace) return;
    if (enlace) {
      if (enlace.target && enlace.target !== '_self') return;
      let url;
      try {
        url = new URL(enlace.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || !RUTAS.includes(sinIdioma(url.pathname))) return;
      if (enlace.closest('dialog[data-nn-test-ventana]')) return;
    }
    if (enElCuestionario() || !dialogo()) return;
    e.preventDefault();
    abrir(boton || enlace);
  });

  document.addEventListener('nn:test:abrir', (e) => {
    if (!enElCuestionario()) abrir(e.target instanceof Element ? e.target : null);
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nn-test-cerrar]')) cerrar();
  });

  // El test pide cerrar (salida de menores, o antes de abrir el cajon).
  document.addEventListener('nn:test:cerrar', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('dialog[data-nn-test-ventana]')) return;
    if (e.detail) e.detail.atendido = true;
    cerrar(e.detail && e.detail.listo);
  });

  // «Añadir» de una tarjeta del resultado: lo mismo que en la pagina y que
  // «Comprar mi rutina», la ventana se cierra y el cajon se abre repintado.
  // Horizon ya lo deja preparado: un alta que sale de un `dialog:modal`
  // espera al `close` de ese dialogo para abrir el cajon (cart-drawer.js);
  // sin cerrarla, el producto entraba en el carrito sin que se viera nada.
  // Lo del propio test (`source: 'natnatura-test'`) ya cierra por su cuenta.
  document.addEventListener('shopify:cart:lines-update', (e) => {
    const d = dialogo();
    if (!d || !d.open || e.action !== 'add') return;
    if (!(e.target instanceof Element) || !d.contains(e.target)) return;
    if (!e.promise || typeof e.promise.then !== 'function') return;
    e.promise
      .then((r) => {
        if (r && r.detail && (r.detail.didError || r.detail.source === 'natnatura-test')) return;
        // Un turno despues, para que el oyente del cajon (que espera el
        // `close`) ya este puesto.
        window.setTimeout(() => cerrar(), 0);
      })
      .catch(() => {});
  });

  // Esc: el navegador dispara `cancel`; se cierra con la animacion.
  document.addEventListener(
    'cancel',
    (e) => {
      if (e.target === dialogo()) {
        e.preventDefault();
        cerrar();
      }
    },
    true,
  );
})();
